import { useState, useCallback } from 'react';
import type { PhotoAttachmentWithUrl } from '@/services/db/dsrDetail';
import { getPhotoSignedUrl } from '@/services/db/dsrDetail';

interface Props {
  photos: PhotoAttachmentWithUrl[];
  entityId: string;
}

export default function PhotoGrid({ photos, entityId }: Props) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lightboxName, setLightboxName] = useState<string>('');

  const entityPhotos = photos.filter((p) => p.entity_id === entityId);

  const handleOpen = useCallback(async (photo: PhotoAttachmentWithUrl) => {
    // Use existing signed URL or generate a fresh one
    const url = photo.signedUrl ?? await getPhotoSignedUrl(photo.storage_path);
    if (url) {
      setLightboxUrl(url);
      setLightboxName(photo.file_name);
    }
  }, []);

  if (entityPhotos.length === 0) return null;

  return (
    <>
      <div className="mt-3">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Photos ({entityPhotos.length})
        </p>
        <div className="flex flex-wrap gap-2">
          {entityPhotos.map((photo) => (
            <button
              key={photo.id}
              type="button"
              onClick={() => handleOpen(photo)}
              className="group relative h-20 w-20 overflow-hidden rounded-md bg-muted transition hover:ring-2 hover:ring-primary sm:h-24 sm:w-24"
            >
              {photo.signedUrl ? (
                <img
                  src={photo.signedUrl}
                  alt={photo.file_name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                  Loading
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox overlay */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setLightboxUrl(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute right-4 top-4 rounded-full bg-black/50 px-3 py-1 text-lg text-white hover:bg-black/70"
            onClick={() => setLightboxUrl(null)}
          >
            ✕
          </button>
          <div className="max-h-[90vh] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightboxUrl}
              alt={lightboxName}
              className="max-h-[85vh] max-w-[85vw] rounded-lg object-contain"
            />
            <p className="mt-2 text-center text-sm text-white/70">{lightboxName}</p>
          </div>
        </div>
      )}
    </>
  );
}
